import L       from 'leaflet';
import { EventBus }  from '../core/EventBus.js';
import { GameState } from '../core/GameState.js';
import { REGION_COLORS } from '../config/ports.js';
import { formatMoney }   from '../utils/formatting.js';
import { calculateRoute } from '../utils/seaRoutes.js';
import { previewEtaText } from '../ui/panels/etaInfo.js';

const SIZE_RADIUS = { tiny: 4, small: 6, medium: 8, large: 11, major: 14 };

// Leaflet's tile layer wraps seamlessly as you pan (no noWrap set in
// MapManager.js), but a plain marker is only ever drawn ONCE, at its raw
// longitude — it does NOT reappear in the adjacent "world copies" you pan
// into. At MIN_ZOOM=2 the whole world is only ~1024px wide, so seeing two
// or more copies side by side (or panning a full wrap past the
// antimeridian to get from, say, Australia back around to the Americas)
// is normal, everyday use of this map — without this, an entire
// hemisphere's ports would just vanish depending on which copy you'd
// panned into. One extra copy on each side covers that normal range;
// every port-anchored marker (the port dot itself, plus its warehouse/
// contract badges) gets three instances, at lng-360/lng/lng+360, all
// driven by the same underlying port so a click/hover/style update on any
// copy behaves identically.
const LNG_WRAP_OFFSETS = [-360, 0, 360];

/**
 * Renders all port markers onto the 'ports' layer.
 */
export class PortMarker {
  constructor(mapLayers) {
    this.mapLayers = mapLayers;
    this.markers   = new Map();  // portId -> L.circleMarker[3] (see LNG_WRAP_OFFSETS)
    this.warehouseMarkers = new Map();  // portId -> L.marker[3] (warehouse badge)
    this.contractMarkers  = new Map();  // portId -> L.marker[3] (contract P/D badge)
    this._dimmedPorts = new Set();  // ports dimmed during route selection

    // Ship being routed, while route-selection mode is active — used so
    // hovering a port's tooltip can show an ETA for THIS ship to THAT port
    // (see _tooltipHtml()). null outside route-selection mode.
    this._routeSelectionShip = null;

    // In route selection mode, unbind tooltips so first tap fires click on mobile
    // Delay to run after dimForShip (which also listens for map:dimPorts)
    EventBus.on('map:dimPorts', ({ ship }) => {
      this._routeSelectionShip = ship || null;
      setTimeout(() => this._setRouteSelectionMode(true), 0);
    });
    EventBus.on('map:resetDim', () => {
      this._routeSelectionShip = null;
      this._setRouteSelectionMode(false);
    });

    // Listen for warehouse events to refresh badges
    EventBus.on('warehouse:built', () => this.refreshWarehouseIndicators());
    EventBus.on('warehouse:upgraded', () => this.refreshWarehouseIndicators());
    EventBus.on('warehouse:cargoTransferred', () => this.refreshWarehouseIndicators());
    EventBus.on('game:loaded', () => { this.refreshWarehouseIndicators(); this.refreshContractBadges(); });
    EventBus.on('game:started', () => { this.refreshWarehouseIndicators(); this.refreshContractBadges(); });

    // Listen for contract events to refresh P/D badges
    EventBus.on('contract:accepted',  () => this.refreshContractBadges());
    EventBus.on('contract:completed', () => this.refreshContractBadges());
    EventBus.on('contract:failed',    () => this.refreshContractBadges());
    EventBus.on('contract:failedBatch', () => this.refreshContractBadges());
    EventBus.on('contracts:updated',  () => this.refreshContractBadges());
  }

  _setRouteSelectionMode(active) {
    // Only unbind tooltips on touch/mobile — desktop keeps hover tooltips during route selection
    const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window;
    if (!isMobile) return;

    for (const [portId, copies] of this.markers) {
      for (const marker of copies) {
        if (active) {
          marker.closeTooltip();
          marker.unbindTooltip();
        } else {
          const port = GameState.getPort(portId);
          if (port) {
            marker.bindTooltip(() => this._tooltipHtml(port), {
              permanent: false,
              direction: 'top',
              className: 'port-tooltip'
            });
          }
        }
      }
    }
  }

  /** Add a single port marker (as three lng-wrapped copies — see LNG_WRAP_OFFSETS) */
  addPort(port) {
    const color  = REGION_COLORS[port.region] || '#888';
    const radius = SIZE_RADIUS[port.size]     || 8;
    const portId = port.id;

    const copies = LNG_WRAP_OFFSETS.map(offset => {
      const marker = L.circleMarker([port.position.lat, port.position.lng + offset], {
        radius,
        color,
        fillColor: color,
        fillOpacity: 0.8,
        weight: 2,
        className: 'port-marker'
      }).addTo(this.mapLayers.getLayer('ports'));

      // Click opens full port panel (or selects destination in route mode)
      marker.on('click', () => {
        if (this._dimmedPorts.has(portId)) return; // blocked — port too small for ship
        EventBus.emit('ui:portSelected', { port: GameState.getPort(portId) || port });
      });

      // Hover tooltip with port summary. Content is a function (not a static
      // string) so it recomputes fresh on every hover — needed for the ETA
      // line below, which depends on which ship (if any) is currently being
      // routed, not just this port's own static data.
      marker.bindTooltip(() => this._tooltipHtml(port), {
        permanent: false,
        direction: 'top',
        className: 'port-tooltip'
      });

      return marker;
    });

    this.markers.set(portId, copies);
  }

  /** Update marker position (ports don't move, but kept for parity) */
  update(port) {
    const copies = this.markers.get(port.id);
    if (!copies) return;
    copies.forEach((m, i) => {
      m.setLatLng([port.position.lat, port.position.lng + LNG_WRAP_OFFSETS[i]]);
      // Update tooltip to reflect current port state (including warehouse).
      // Function content, not a static string — see addPort()'s comment.
      m.setTooltipContent(() => this._tooltipHtml(port));
    });
  }

  /** Dim ports that cannot accept the given ship type */
  dimForShip(shipType) {
    this._dimmedPorts.clear();
    for (const [portId, copies] of this.markers) {
      const port = GameState.getPort(portId);
      if (port && !port.canAcceptShip(shipType)) {
        for (const marker of copies) marker.setStyle({ fillOpacity: 0.15, opacity: 0.2 });
        this._dimmedPorts.add(portId);
      }
    }
  }

  /**
   * Briefly pulse a ring around a port so it's easy to spot after the map
   * pans to it (e.g. clicking a port name in a panel). One flash at a time —
   * a new one replaces any still running.
   */
  flash(portId) {
    const port = GameState.getPort(portId);
    if (!port?.position) return;
    this._clearFlash();

    const size = (SIZE_RADIUS[port.size] || 8) * 2 + 8;
    const icon = L.divIcon({
      html: '<div class="port-flash-ring"></div>',
      className: 'port-flash-icon',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
    const layer = this.mapLayers.getLayer('selection');
    this._flashMarkers = LNG_WRAP_OFFSETS.map(offset =>
      L.marker([port.position.lat, port.position.lng + offset], { icon, interactive: false, keyboard: false }).addTo(layer)
    );
    this._flashTimer = setTimeout(() => this._clearFlash(), 2600);
  }

  _clearFlash() {
    clearTimeout(this._flashTimer);
    for (const m of this._flashMarkers || []) m.remove();
    this._flashMarkers = null;
  }

  /** Restore all port markers to full opacity */
  resetDim() {
    this._dimmedPorts.clear();
    for (const copies of this.markers.values()) {
      for (const marker of copies) marker.setStyle({ fillOpacity: 0.8, opacity: 1 });
    }
  }

  /** Refresh warehouse indicator badges for all ports with warehouses */
  refreshWarehouseIndicators() {
    // Get current warehouses from GameState
    const warehouses = GameState.warehouses || new Map();
    const warehousePortIds = new Set();

    // Update or create badges for existing warehouses
    for (const warehouse of warehouses.values()) {
      const portId = warehouse.portId;
      warehousePortIds.add(portId);

      const port = GameState.getPort(portId);
      if (!port) continue;

      const existingBadges = this.warehouseMarkers.get(portId);

      if (existingBadges) {
        // Update existing badges
        const icon = this._createWarehouseBadgeIcon(warehouse.level);
        for (const badge of existingBadges) badge.setIcon(icon);
      } else {
        // Create new badges (one per lng-wrapped copy)
        const badges = LNG_WRAP_OFFSETS.map(offset => {
          const badge = L.marker(
            [port.position.lat, port.position.lng + offset],
            {
              icon: this._createWarehouseBadgeIcon(warehouse.level),
              interactive: true,
              zIndexOffset: 100  // Appear above port markers
            }
          ).addTo(this.mapLayers.getLayer('ports'));

          // Click badge to select port
          badge.on('click', () => {
            EventBus.emit('ui:portSelected', { port: GameState.getPort(portId) || port });
          });

          return badge;
        });

        this.warehouseMarkers.set(portId, badges);
      }

      // Update the port marker tooltip to include warehouse info
      const copies = this.markers.get(portId);
      if (copies) {
        for (const marker of copies) marker.setTooltipContent(() => this._tooltipHtml(port));
      }
    }

    // Remove badges for warehouses that no longer exist
    for (const [portId, badges] of this.warehouseMarkers) {
      if (!warehousePortIds.has(portId)) {
        for (const badge of badges) badge.remove();
        this.warehouseMarkers.delete(portId);

        // Update tooltip to remove warehouse info
        const port = GameState.getPort(portId);
        const copies = this.markers.get(portId);
        if (copies && port) {
          for (const marker of copies) marker.setTooltipContent(() => this._tooltipHtml(port));
        }
      }
    }
  }

  /** Create a divIcon for warehouse badge with level-based styling */
  _createWarehouseBadgeIcon(level) {
    const levelClass = `warehouse-badge-level-${level}`;
    const html = `<div class="warehouse-badge ${levelClass}">W${level}</div>`;

    return L.divIcon({
      html,
      className: 'warehouse-badge-icon',
      iconSize: [18, 18],
      iconAnchor: [0, 0]  // Top-left offset from port center
    });
  }

  /** Refresh contract P (pickup) and D (delivery) badges for active contracts */
  refreshContractBadges() {
    const activeContracts = GameState.contracts?.active || [];

    // Build map of portId -> badge type ('P', 'D', or 'PD')
    const contractPorts = new Map();
    for (const c of activeContracts) {
      if (!c.cargoLoaded) {
        const prev = contractPorts.get(c.originPort) || '';
        if (!prev.includes('P')) contractPorts.set(c.originPort, prev + 'P');
      }
      const prev = contractPorts.get(c.destinationPort) || '';
      if (!prev.includes('D')) contractPorts.set(c.destinationPort, prev + 'D');
    }

    // Update or create badges
    for (const [portId, types] of contractPorts) {
      const port = GameState.getPort(portId);
      if (!port) continue;

      const existing = this.contractMarkers.get(portId);
      const icon = this._createContractBadgeIcon(types);

      if (existing) {
        for (const badge of existing) badge.setIcon(icon);
      } else {
        const badges = LNG_WRAP_OFFSETS.map(offset => {
          const badge = L.marker(
            [port.position.lat, port.position.lng + offset],
            { icon, interactive: true, zIndexOffset: 150 }
          ).addTo(this.mapLayers.getLayer('ports'));

          badge.on('click', () => {
            EventBus.emit('ui:portSelected', { port: GameState.getPort(portId) || port });
          });

          return badge;
        });

        this.contractMarkers.set(portId, badges);
      }
    }

    // Remove badges for ports no longer in active contracts
    for (const [portId, badges] of this.contractMarkers) {
      if (!contractPorts.has(portId)) {
        for (const badge of badges) badge.remove();
        this.contractMarkers.delete(portId);
      }
    }
  }

  _createContractBadgeIcon(types) {
    // 'P' = pickup (blue), 'D' = delivery (green), 'PD' = both
    const label = types.length > 1 ? 'PD' : types;
    const cls = types.includes('P') && types.includes('D')
      ? 'contract-badge-pd'
      : types.includes('P') ? 'contract-badge-pickup' : 'contract-badge-delivery';

    return L.divIcon({
      html: `<div class="contract-badge ${cls}">${label}</div>`,
      className: 'contract-badge-icon',
      iconSize: [18, 18],
      iconAnchor: [-10, 0]  // Offset right of warehouse badges
    });
  }

  _tooltipHtml(port) {
    let html = `<strong>${port.name}</strong><br>${port.country} &mdash; ${port.size}<br>Fuel: $${port.fuelPrice}/t &nbsp; Dock: ${formatMoney(port.dockingFee)}`;

    // Add warehouse info if one exists at this port
    // Warehouses use 'wh_' + portId as their ID
    const warehouse = GameState.warehouses?.get('wh_' + port.id);
    if (warehouse) {
      const usage = warehouse.cargoUsed || 0;
      const capacity = warehouse.capacity || 0;
      const percent = capacity > 0 ? Math.round((usage / capacity) * 100) : 0;
      html += `<br><span class="warehouse-info">Warehouse L${warehouse.level}: ${usage}/${capacity} TEU (${percent}%)</span>`;
    }

    // Route-selection mode (UIManager.startRouteSelection): show an ETA
    // for the ship actually being routed, to THIS port. Recomputed live
    // per-hover since this is bound as a tooltip content function (see
    // addPort()) — the ship keeps sailing/moving while the player browses
    // destinations, so a stale distance would drift.
    const ship = this._routeSelectionShip;
    if (ship && ship.position && port.id !== ship.currentPortId) {
      const { distance } = calculateRoute(ship.position, port.position);
      const eta = previewEtaText(ship, distance);
      if (eta) html += `<br><span class="route-eta">ETA: ${eta}</span>`;
    }

    return html;
  }
}
